import React, {
  createContext, useContext, useEffect, useState, useCallback, useMemo, useRef
} from 'react';
import { customerSupabase as supabase } from '../customerSupabase';
import apiClient from '../utils/apiClient';
import { isCustomerProfileComplete } from '../utils/customerProfile';

/**
 * CustomerContext
 * ---------------
 * Single source of truth for the customer session, profile, and delivery
 * addresses. This is the ONLY place that subscribes to Supabase auth changes
 * for the customer, which removes the duplicate listeners that previously
 * lived inside individual pages.
 *
 * Responsibilities:
 *  - Restore / track the Supabase customer session (one listener).
 *  - Load the customer profile once per session.
 *  - Load saved addresses (from the existing `saved_addresses` table via API).
 *  - Track the globally selected delivery address and persist it.
 *  - Expose drawer open/close state for the mandatory address selector.
 */

const CustomerContext = createContext(null);

// localStorage: the last address the customer chose (used to pre-highlight).
const REMEMBER_KEY = (userId) => `niva_selected_address_${userId}`;
// sessionStorage: the address confirmed during THIS browser session. This is
// what keeps the drawer closed across refreshes but re-opens it on a fresh
// visit so the customer always confirms where their order is going.
const SESSION_KEY = (userId) => `niva_addr_session_${userId}`;

function normalizeProfile(record) {
  if (!record) return null;
  return {
    name: record.name || 'User',
    email: record.email || '',
    phone: record.phone || '',
    college: record.college || '',
    university: record.college || 'Select your university',
    location: record.college || 'Your Location'
  };
}

export function CustomerProvider({ children }) {
  const [authInitialized, setAuthInitialized] = useState(false);
  const [user, setUser] = useState(null);

  const [profileRecord, setProfileRecord] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesLoaded, setAddressesLoaded] = useState(false);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressDrawerOpen, setAddressDrawerOpen] = useState(false);

  const userId = user?.id || null;
  const isAuthenticated = !!user;

  // Keep the latest userId available inside async callbacks without re-subscribing.
  const userIdRef = useRef(null);
  userIdRef.current = userId;

  // ---- Auth: single listener -------------------------------------------------
  useEffect(() => {
    let mounted = true;

    const applySession = (session) => {
      if (!mounted) return;
      const nextUser = session?.user || null;
      if (nextUser) {
        localStorage.setItem('userId', nextUser.id);
        localStorage.setItem('userRole', 'customer');
      }
      setUser(nextUser);
      setAuthInitialized(true);
    };

    supabase.auth.getSession().then(({ data }) => applySession(data?.session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
      // Reset per-user derived state so a fresh login reloads cleanly.
      if (!session?.user) {
        setProfileRecord(null);
        setAddresses([]);
        setAddressesLoaded(false);
        setSelectedAddress(null);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // ---- Profile ---------------------------------------------------------------
  const refreshProfile = useCallback(async (id = userIdRef.current) => {
    if (!id) return null;
    setProfileLoading(true);
    try {
      const res = await apiClient.get(`/api/customers/${id}`);
      const record = res?.data?.data || null;
      setProfileRecord(record);
      return record;
    } catch (err) {
      console.error('Failed to load customer profile:', err);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authInitialized) return;
    if (userId) {
      const cached = sessionStorage.getItem('userProfile');
      if (cached && !profileRecord) {
        try { setProfileRecord(JSON.parse(cached)); } catch { /* ignore */ }
      }
      refreshProfile(userId);
    } else {
      setProfileRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, userId, refreshProfile]);

  // Cache normalized profile for pages that read it synchronously on mount.
  useEffect(() => {
    const normalized = normalizeProfile(profileRecord);
    if (normalized) sessionStorage.setItem('userProfile', JSON.stringify(normalized));
  }, [profileRecord]);

  // Allow legacy profile-update events to refresh context state.
  useEffect(() => {
    const handler = (event) => {
      if (event.detail) setProfileRecord((prev) => ({ ...(prev || {}), ...event.detail }));
    };
    window.addEventListener('profileUpdated', handler);
    return () => window.removeEventListener('profileUpdated', handler);
  }, []);

  // ---- Addresses -------------------------------------------------------------
  const persistSelection = useCallback((id, address) => {
    if (!id) return;
    try {
      if (address) {
        localStorage.setItem(REMEMBER_KEY(id), address.id);
        sessionStorage.setItem(SESSION_KEY(id), address.id);
      } else {
        localStorage.removeItem(REMEMBER_KEY(id));
        sessionStorage.removeItem(SESSION_KEY(id));
      }
    } catch { /* ignore */ }
  }, []);

  // A selection is only "restored" when it was confirmed in this session. We
  // deliberately do NOT auto-pick the default here, so a logged-in customer is
  // asked to confirm their delivery address each time they open the site.
  const resolveSelection = useCallback((id, list) => {
    let chosen = null;
    try {
      const sessionId = sessionStorage.getItem(SESSION_KEY(id));
      if (sessionId) chosen = list.find((a) => a.id === sessionId) || null;
    } catch { /* ignore */ }
    setSelectedAddress(chosen);
    return chosen;
  }, []);

  const refreshAddresses = useCallback(async (id = userIdRef.current) => {
    if (!id) return [];
    setAddressesLoading(true);
    try {
      const res = await apiClient.get(`/api/customers/${id}/addresses`);
      const list = res?.data?.data || [];
      setAddresses(list);
      resolveSelection(id, list);
      setAddressesLoaded(true);
      return list;
    } catch (err) {
      console.error('Failed to load saved addresses:', err);
      setAddressesLoaded(true);
      return [];
    } finally {
      setAddressesLoading(false);
    }
  }, [resolveSelection]);

  useEffect(() => {
    if (!authInitialized) return;
    if (userId) {
      refreshAddresses(userId);
    } else {
      setAddresses([]);
      setSelectedAddress(null);
      setAddressesLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, userId, refreshAddresses]);

  const selectAddress = useCallback((address) => {
    setSelectedAddress(address);
    persistSelection(userIdRef.current, address);
    setAddressDrawerOpen(false);
    // Let dependent pages (checkout, delivery calc) recompute.
    window.dispatchEvent(new CustomEvent('deliveryAddressChanged', { detail: address }));
  }, [persistSelection]);

  const addAddress = useCallback(async (payload) => {
    const id = userIdRef.current;
    if (!id) throw new Error('You need to be logged in to save an address.');

    const res = await apiClient.post(`/api/customers/${id}/addresses`, payload);
    const created = res?.data?.data;
    if (!res?.data?.success || !created) {
      throw new Error(res?.data?.message || 'Could not save the address.');
    }

    // Refresh list, then auto-select the newly created address.
    const list = await refreshAddresses(id);
    const fresh = list.find((a) => a.id === created.id) || created;
    setSelectedAddress(fresh);
    persistSelection(id, fresh);
    window.dispatchEvent(new CustomEvent('deliveryAddressChanged', { detail: fresh }));
    return fresh;
  }, [refreshAddresses, persistSelection]);

  const updateAddress = useCallback(async (addressId, payload) => {
    const id = userIdRef.current;
    if (!id) throw new Error('You need to be logged in.');

    const res = await apiClient.put(`/api/customers/${id}/addresses/${addressId}`, payload);
    if (!res?.data?.success) {
      throw new Error(res?.data?.message || 'Could not update the address.');
    }

    await refreshAddresses(id);
    return res.data.data;
  }, [refreshAddresses]);

  const deleteAddress = useCallback(async (addressId) => {
    const id = userIdRef.current;
    if (!id) throw new Error('You need to be logged in.');

    const res = await apiClient.delete(`/api/customers/${id}/addresses/${addressId}`);
    if (!res?.data?.success) {
      throw new Error(res?.data?.message || 'Could not delete the address.');
    }

    // If this was the selected address, clear selection.
    if (selectedAddress?.id === addressId) {
      setSelectedAddress(null);
      persistSelection(id, null);
    }
    await refreshAddresses(id);
  }, [refreshAddresses, persistSelection, selectedAddress]);

  const setDefaultAddress = useCallback(async (addressId) => {
    const id = userIdRef.current;
    if (!id) throw new Error('You need to be logged in.');

    const res = await apiClient.put(`/api/customers/${id}/addresses/${addressId}/default`);
    if (!res?.data?.success) {
      throw new Error(res?.data?.message || 'Could not set default address.');
    }

    await refreshAddresses(id);
    return res.data.data;
  }, [refreshAddresses]);

  const openAddressDrawer = useCallback(() => setAddressDrawerOpen(true), []);
  const closeAddressDrawer = useCallback(() => setAddressDrawerOpen(false), []);

  const logout = useCallback(async () => {
    const id = userIdRef.current;
    if (id) persistSelection(id, null);
    await supabase.auth.signOut();
    localStorage.removeItem('userId');
    localStorage.removeItem('userRole');
    sessionStorage.removeItem('userProfile');
  }, [persistSelection]);

  // Address to pre-highlight in the drawer: last-used, else the default.
  const preferredAddress = useMemo(() => {
    if (!userId) return null;
    let rememberedId = null;
    try { rememberedId = localStorage.getItem(REMEMBER_KEY(userId)); } catch { /* ignore */ }
    return addresses.find((a) => a.id === rememberedId)
      || addresses.find((a) => a.is_default)
      || null;
  }, [addresses, userId]);

  const profile = useMemo(() => normalizeProfile(profileRecord), [profileRecord]);
  const profileComplete = useMemo(
    () => isCustomerProfileComplete(profileRecord),
    [profileRecord]
  );

  // The mandatory selector must appear when a logged-in customer has finished
  // loading their addresses but still has no delivery address selected.
  const mustSelectAddress = isAuthenticated && addressesLoaded && !selectedAddress;

  const value = useMemo(() => ({
    // auth
    authInitialized,
    isAuthenticated,
    user,
    userId,
    logout,
    // profile
    profile,
    profileRecord,
    profileLoading,
    profileComplete,
    refreshProfile,
    // addresses
    addresses,
    addressesLoading,
    addressesLoaded,
    refreshAddresses,
    selectedAddress,
    preferredAddress,
    selectAddress,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
    // drawer
    addressDrawerOpen,
    openAddressDrawer,
    closeAddressDrawer,
    mustSelectAddress
  }), [
    authInitialized, isAuthenticated, user, userId, logout,
    profile, profileRecord, profileLoading, profileComplete, refreshProfile,
    addresses, addressesLoading, addressesLoaded, refreshAddresses,
    selectedAddress, preferredAddress, selectAddress, addAddress,
    updateAddress, deleteAddress, setDefaultAddress,
    addressDrawerOpen, openAddressDrawer, closeAddressDrawer, mustSelectAddress
  ]);

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return ctx;
}

export default CustomerContext;
